import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { User, Mail, Lock, Eye, EyeOff, ArrowLeft } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { PeepingLogo } from "@/components/PeepingLogo";
import { Checkbox } from "@/components/ui/checkbox";

export default function Register() {
  const { register, formatApiErrorDetail } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ username: "", email: "", password: "", confirm: "" });
  const [accepted, setAccepted] = useState(false);
  const [show, setShow] = useState(false);
  const [errors, setErrors] = useState({});
  const [apiError, setApiError] = useState("");
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const validate = () => {
    const er = {};
    if (form.username.trim().length < 3) er.username = "Username must be at least 3 characters.";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) er.email = "Enter a valid email address.";
    if (form.password.length < 8) er.password = "Password must be at least 8 characters.";
    if (form.confirm !== form.password) er.confirm = "Passwords do not match.";
    if (!accepted) er.terms = "You must accept the Terms to continue.";
    setErrors(er);
    return Object.keys(er).length === 0;
  };

  const submit = async (e) => {
    e.preventDefault();
    setApiError("");
    if (!validate()) return;
    setLoading(true);
    try {
      await register(form.username.trim(), form.email.trim(), form.password);
      navigate("/app/home", { replace: true });
    } catch (err) {
      setApiError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="register-screen" className="relative min-h-screen bg-[#080808] grain">
      <div className="absolute -top-16 -right-10 h-56 w-56 glow-violet opacity-40" />
      <div className="mx-auto flex min-h-screen w-full max-w-md flex-col px-6 pb-10 pt-8">
        <div className="flex items-center justify-between">
          <Link to="/welcome" data-testid="register-back" className="text-[#a7a0af] hover:text-white">
            <ArrowLeft size={22} />
          </Link>
          <span className="text-xs text-[#a7a0af]">Step 1 of 3</span>
        </div>

        <div className="mt-8 flex flex-col items-center">
          <PeepingLogo className="w-44" />
        </div>

        <div className="mt-8">
          <h1 className="text-2xl font-bold">Create Your Account</h1>
          <p className="mt-1 text-sm text-[#a7a0af]">A more personal experience awaits.</p>
        </div>

        <form onSubmit={submit} className="mt-6 flex flex-col gap-3" noValidate>
          <Field icon={User} error={errors.username} label="Username">
            <input
              data-testid="register-username"
              value={form.username}
              onChange={set("username")}
              placeholder="Choose a username"
              aria-label="Username"
              className="field w-full rounded-2xl py-4 pl-11 pr-4 text-sm"
            />
          </Field>
          <Field icon={Mail} error={errors.email} label="Email address">
            <input
              data-testid="register-email"
              type="email"
              value={form.email}
              onChange={set("email")}
              placeholder="you@example.com"
              aria-label="Email address"
              className="field w-full rounded-2xl py-4 pl-11 pr-4 text-sm"
            />
          </Field>
          <Field icon={Lock} error={errors.password} label="Password">
            <input
              data-testid="register-password"
              type={show ? "text" : "password"}
              value={form.password}
              onChange={set("password")}
              placeholder="At least 8 characters"
              aria-label="Password"
              className="field w-full rounded-2xl py-4 pl-11 pr-11 text-sm"
            />
            <button type="button" onClick={() => setShow(!show)} className="absolute right-4 top-1/2 -translate-y-1/2 text-[#a7a0af]" aria-label="Toggle password">
              {show ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </Field>
          <Field icon={Lock} error={errors.confirm} label="Confirm password">
            <input
              data-testid="register-confirm"
              type={show ? "text" : "password"}
              value={form.confirm}
              onChange={set("confirm")}
              placeholder="Re-enter your password"
              aria-label="Confirm password"
              className="field w-full rounded-2xl py-4 pl-11 pr-4 text-sm"
            />
          </Field>

          <label className="mt-1 flex items-start gap-3 text-xs text-white/80 cursor-pointer">
            <Checkbox
              checked={accepted}
              onCheckedChange={(v) => setAccepted(!!v)}
              data-testid="register-terms"
              className="mt-0.5 border-white/40 data-[state=checked]:bg-[#d42be8] data-[state=checked]:border-[#d42be8]"
            />
            <span>
              I am 18+ and agree to the{" "}
              <Link to="/legal/terms" className="text-[#d42be8]">Terms</Link> and{" "}
              <Link to="/legal/privacy" className="text-[#d42be8]">Privacy Policy</Link>.
            </span>
          </label>
          {errors.terms && <p className="text-xs text-red-400" data-testid="register-terms-error">{errors.terms}</p>}

          {apiError && (
            <div data-testid="register-api-error" className="rounded-xl bg-red-500/10 border border-red-500/30 px-4 py-3 text-sm text-red-300">
              <p>{apiError}</p>
              {/already exists|taken/i.test(apiError) && (
                <p className="mt-2 text-xs text-white/80">
                  <Link to="/login" data-testid="register-error-login" className="font-semibold text-[#d42be8] underline">Log in</Link>
                  {" "}or{" "}
                  <Link to="/forgot-password" data-testid="register-error-forgot" className="font-semibold text-[#d42be8] underline">reset your password</Link>.
                </p>
              )}
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            data-testid="register-submit"
            className="btn-brand mt-2 w-full rounded-2xl py-4 text-base font-semibold text-white"
          >
            {loading ? "Creating…" : "Continue"}
          </button>
        </form>

        <p className="mt-6 text-center text-sm text-[#a7a0af]">
          Already have an account?{" "}
          <Link to="/login" data-testid="register-login-link" className="font-semibold text-[#d42be8]">
            Log in
          </Link>
        </p>
      </div>
    </div>
  );
}

function Field({ icon: Icon, error, label, children }) {
  return (
    <div>
      {label && <label className="mb-1 block pl-1 text-xs font-medium text-[#a7a0af]">{label}</label>}
      <div className="relative">
        <Icon size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#a7a0af]" />
        {children}
      </div>
      {error && <p className="mt-1 pl-1 text-xs text-red-400">{error}</p>}
    </div>
  );
}
